/**
 * WebSocket 연결 훅
 * 실시간 협업 세션 관리
 */
import { useState, useEffect, useRef, useCallback } from 'react';

interface WebSocketMessage {
    type: string;
    data?: any;
    user_id?: string;
    timestamp?: string;
}

interface UseWebSocketOptions {
    sessionId: string;
    userId: string;
    token?: string;
    onMessage?: (message: WebSocketMessage) => void;
    onConnect?: () => void;
    onDisconnect?: () => void;
    autoReconnect?: boolean;
}

export function useWebSocket({
    sessionId,
    userId,
    token,
    onMessage,
    onConnect,
    onDisconnect,
    autoReconnect = true,
}: UseWebSocketOptions) {
    const [isConnected, setIsConnected] = useState(false);
    const [participants, setParticipants] = useState<any[]>([]);
    const wsRef = useRef<WebSocket | null>(null);
    const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const reconnectAttemptsRef = useRef(0);
    const MAX_RECONNECT_ATTEMPTS = 5;

    const getWsUrl = useCallback(() => {
        const hostname = window.location.hostname;
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const port = hostname === 'localhost' || hostname === '127.0.0.1' ? ':8001' : ':8001';
        const params = new URLSearchParams({
            user_id: userId,
            ...(token ? { token } : {}),
        });
        return `${protocol}//${hostname}${port}/api/collaboration/sessions/${sessionId}/ws?${params}`;
    }, [sessionId, userId, token]);

    const connect = useCallback(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) return;

        const ws = new WebSocket(getWsUrl());

        ws.onopen = () => {
            setIsConnected(true);
            reconnectAttemptsRef.current = 0;
            onConnect?.();
        };

        ws.onmessage = (event) => {
            try {
                const message: WebSocketMessage = JSON.parse(event.data);

                if (message.type === 'participant_joined' || message.type === 'participant_left') {
                    setParticipants(message.data?.participants || []);
                }

                onMessage?.(message);
            } catch (e) {
                console.error('WebSocket message parse error:', e);
            }
        };

        ws.onclose = () => {
            setIsConnected(false);
            onDisconnect?.();

            if (autoReconnect && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
                const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
                reconnectTimeoutRef.current = setTimeout(() => {
                    reconnectAttemptsRef.current++;
                    connect();
                }, delay);
            }
        };

        ws.onerror = () => {
            ws.close();
        };

        wsRef.current = ws;
    }, [getWsUrl, onMessage, onConnect, onDisconnect, autoReconnect]);

    const disconnect = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }
        reconnectAttemptsRef.current = MAX_RECONNECT_ATTEMPTS; // prevent reconnect
        wsRef.current?.close();
    }, []);

    const sendMessage = useCallback((type: string, data?: any) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type, data, timestamp: new Date().toISOString() }));
        }
    }, []);

    const sendDocumentChange = useCallback((change: any) => {
        sendMessage('document_change', change);
    }, [sendMessage]);

    const sendCursorUpdate = useCallback((position: any) => {
        sendMessage('cursor_update', position);
    }, [sendMessage]);

    const sendComment = useCallback((comment: any) => {
        sendMessage('add_comment', comment);
    }, [sendMessage]);

    useEffect(() => {
        if (sessionId && userId) {
            connect();
        }
        return () => {
            disconnect();
        };
    }, [sessionId, userId]);

    return {
        isConnected,
        participants,
        sendMessage,
        sendDocumentChange,
        sendCursorUpdate,
        sendComment,
        connect,
        disconnect,
    };
}
